import { Outlet, Link, useNavigate, useLocation, useParams } from "react-router-dom";
import BrandMark from "@/components/BrandMark";
import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { API, api, fetchAll, formatError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { StatusPill } from "@/components/StatusPill";
import FileRow from "@/components/FileRow";
import ChatThread from "@/components/ChatThread";
import { useCommentsPoll } from "@/lib/useCommentsPoll";
import { useScrollToHash } from "@/lib/useScrollToHash";
import SiteFooter from "@/components/SiteFooter";
import { toast } from "sonner";
import { LogOut, Upload, Download, MessageSquare, ChevronLeft } from "lucide-react";

export function AdvocateLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const initials = (user?.name || "?").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase();
  return (
    <div className="min-h-screen bg-[#080C16] text-[#F9F8F6]">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#080C16]/80 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/advocate" className="flex items-center gap-2 font-serif-display text-lg tracking-tight">
            <BrandMark /> DharmaTN
            <span className="text-[9px] tracking-[0.24em] uppercase text-[#6B7280] ml-1">Advocate</span>
          </Link>
          <div className="flex items-center gap-3 text-sm">
            <div className="w-7 h-7 rounded-full bg-[#CBA96C] text-[#080C16] flex items-center justify-center font-medium text-[10px]">{initials}</div>
            <span className="text-[#A0AABF] hidden sm:inline">{user?.name}</span>
            <button
              data-testid="advocate-logout"
              // Navigate first, then clear — see the signOut comment in CustomerLayout.jsx.
              onClick={async()=>{ nav("/", { replace: true }); await logout(); }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs text-[#A0AABF] hover:text-white hover:bg-white/5 transition-colors"
            ><LogOut className="w-3.5 h-3.5"/>Sign out</button>
          </div>
        </div>
      </header>
      {/* An advocate still carrying the password admin handed them gets this and nothing else.
          Rendered *instead of* the outlet rather than as a route of its own, so there is no
          /advocate/* URL that reaches past it — the same shape as the server-side gate in
          core/security.py, which refuses every reviewer route while the flag is set. A route plus
          a redirect would be two rules that could disagree; this is one. */}
      <div className="max-w-7xl mx-auto px-6 py-10">
        {user?.must_change_password ? <AdvocateChangePassword/> : <Outlet/>}
      </div>
      <SiteFooter/>
    </div>
  );
}

/**
 * The forced password change. Shown to an advocate whose account is still on the password admin
 * gave them; there is no way past it and nothing else in the portal will answer until it is done
 * (`PUT /auth/password` clears the flag, and `refresh()` re-reads the session so the outlet
 * appears without a reload).
 */
export function AdvocateChangePassword() {
  const { refresh } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  // Checked here as well as server-side so the advocate is told before a round-trip; the server
  // remains the only enforcement (core/models.py ChangePasswordIn, and the default-password check
  // in routers/auth.py).
  const tooShort = next.length > 0 && next.length < 8;
  const mismatch = confirm.length > 0 && next !== confirm;
  const ready = current && next.length >= 8 && next === confirm && !busy;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/auth/password", { current_password: current, new_password: next });
      toast.success("Password changed — welcome to your workspace.");
      await refresh();
    } catch (err) {
      toast.error(formatError(err?.response?.data?.detail) || "Could not change the password");
    } finally { setBusy(false); }
  };

  const field = (label, value, setter, testid, autoComplete) => (
    <div>
      <Label className="text-[#A0AABF] text-xs uppercase tracking-[0.18em]">{label}</Label>
      <Input
        data-testid={testid} type="password" required value={value} autoComplete={autoComplete}
        onChange={(e) => setter(e.target.value)}
        className="mt-2 bg-[#080C16] border-white/10 text-white focus:border-[#CBA96C] focus:ring-1 focus:ring-[#CBA96C]"
      />
    </div>
  );

  return (
    <div className="max-w-md mx-auto" data-testid="advocate-change-password">
      <div className="text-[10px] tracking-[0.32em] uppercase text-[#A0AABF] mb-3">One more step</div>
      <h1 className="font-serif-display text-3xl leading-tight tracking-tight mb-3">
        Choose your password
      </h1>
      <p className="text-sm text-[#A0AABF] leading-relaxed mb-8">
        Your account was created with a temporary password that is the same for every new advocate.
        Replace it before you start — your cases are not available until you do.
      </p>
      <form onSubmit={submit} className="space-y-4 bg-[#111623]/70 border border-white/10 rounded-2xl p-6">
        {field("Temporary password", current, setCurrent, "advocate-current-password", "current-password")}
        {field("New password", next, setNext, "advocate-new-password", "new-password")}
        {field("Confirm new password", confirm, setConfirm, "advocate-confirm-password", "new-password")}
        {tooShort && (
          <p data-testid="advocate-password-tooshort" className="text-xs text-[#F5A524]">
            At least 8 characters.
          </p>
        )}
        {mismatch && (
          <p data-testid="advocate-password-mismatch" className="text-xs text-[#F5A524]">
            The two new passwords do not match.
          </p>
        )}
        <Button
          data-testid="advocate-change-password-submit" disabled={!ready}
          className="w-full bg-[#CBA96C] text-[#080C16] hover:bg-[#DBC190] font-medium h-11 rounded-lg disabled:opacity-50"
        >{busy ? "Saving…" : "Set password and continue"}</Button>
      </form>
    </div>
  );
}

export function AdvocateDashboard() {
  const [rows, setRows] = useState([]);
  useEffect(() => { fetchAll("/reviewer/transactions").then(setRows).catch(() => {}); }, []);
  return (
    <div className="space-y-6" data-testid="advocate-dashboard">
      <div>
        <div className="text-[10px] tracking-[0.32em] uppercase text-[#CBA96C]">Case Queue</div>
        <h1 className="font-serif-display text-4xl text-white mt-1">Assigned Cases</h1>
      </div>
      <div className="bg-[#111623] border border-white/10 rounded-2xl overflow-hidden">
        <div className="hidden md:grid grid-cols-[110px_1fr_1fr_120px_120px] gap-4 px-5 py-3 border-b border-white/5 text-[10px] tracking-[0.24em] uppercase text-[#6B7280]">
          <div>Txn</div><div>Customer</div><div>Service</div><div>Status</div><div className="text-right">Action</div>
        </div>
        {rows.length === 0 && (
          <div className="p-10 text-center text-[#6B7280] text-sm">No cases assigned yet.</div>
        )}
        {rows.map(t => {
          const items = (t.action_items || []).filter(i => i.owner === "reviewer");
          return (
            <div key={t.id} data-testid={`adv-txn-${t.id}`} className="grid md:grid-cols-[110px_1fr_1fr_120px_120px] gap-4 px-5 py-4 border-b border-white/5 last:border-0 items-center hover:bg-white/[0.02] transition-colors">
              <div className="font-mono text-xs text-[#A0AABF]">{t.id.slice(0,8)}</div>
              <div className="min-w-0">
                <div className="text-sm text-white truncate">{t.customer_name}</div>
                {items.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {items.map(i => <span key={i.reason} className="pill pill-xs pill-owner-reviewer">{i.label}</span>)}
                  </div>
                )}
              </div>
              <div className="text-sm text-white/90">{t.template_title || t.service_type.replace(/_/g,' ')}</div>
              <div><StatusPill status={t.status} dim="status"/></div>
              <div className="text-right">
                <Link to={`/advocate/case/${t.id}`}>
                  <Button data-testid={`open-case-${t.id}`} size="sm" className="bg-[#CBA96C] text-[#080C16] hover:bg-[#DBC190] font-medium h-8 px-3">Open</Button>
                </Link>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function AdvocateCase() {
  const { id } = useParams();
  const [t, setT] = useState(null);
  const [comments, setComments] = useState([]);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  // structured findings — see LIFECYCLE.md "Reviewer findings". Submitted as data rather than
  // prose so admin can apply them to the document in one click instead of retyping a PDF.
  const [summary, setSummary] = useState("");
  const [clauses, setClauses] = useState("");
  const [corrections, setCorrections] = useState({});
  const load = () => {
    api.get(`/transactions/${id}`).then(r => setT(r.data)).catch(() => {});
    api.get(`/transactions/${id}/comments`).then(r => setComments(r.data)).catch(() => {});
    api.get(`/transactions/${id}/messages`).then(r => setMessages(r.data)).catch(() => {});
  };
  useEffect(load, [id]);
  // Live thread: without this, a message the customer or admin sent only appeared after the
  // advocate manually reloaded the page.
  useCommentsPoll(id, setComments);
  useScrollToHash(!!t);
  useEffect(() => {
    if (!id) return;
    api.post(`/transactions/${id}/viewed`).catch(() => {});
  }, [id]);
  // One merged, time-ordered conversation instead of two separate panels (relay comments +
  // admin's one-way quick-messages) — quick-messages are mapped into the same comment shape,
  // always "moderated" (they're a direct broadcast, never held for release) and carrying their
  // own `unread` flag so ChatThread can still show a "New" marker until the reviewer replies.
  const thread = [...comments,
    ...messages.map(m => ({
      id: `qm-${m.id}`, author_role: "admin", author_name: "Admin",
      text: m.text, ts: m.ts, moderated: true, unread: !m.read,
    }))].sort((a, b) => new Date(a.ts) - new Date(b.ts));
  const post = async () => { if(!text.trim())return; await api.post(`/transactions/${id}/comments`, { text }); setText(""); load(); toast.success("Sent"); };
  const upload = async () => {
    if (!file) return toast.error("Select a file");
    const fd = new FormData(); fd.append("file", file); fd.append("kind","report");
    await api.post(`/transactions/${id}/upload`, fd, { headers: {"Content-Type":"multipart/form-data"}});
    toast.success("Report uploaded"); setFile(null); load();
  };
  const submitReview = async () => {
    if (!summary.trim()) return toast.error("Add a short summary of your conclusion");
    try {
      const clean = Object.fromEntries(Object.entries(corrections).filter(([, v]) => String(v ?? "").trim() !== ""));
      await api.post(`/reviewer/transactions/${id}/submit-review`, {
        summary, form_data_corrections: clean, recommended_clauses: clauses,
      });
      toast.success("Review submitted to admin");
      setSummary(""); setClauses(""); setCorrections({}); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Submit failed"); }
  };
  if (!t) return <div className="text-[#A0AABF]">Loading…</div>;
  const tok = localStorage.getItem("dt_token");
  const rs = t.review_status || "not_started";
  const caseClosed = ["closed", "cancelled"].includes(t.status);
  const awaitingAdmin = rs === "submitted";
  const reviewDone = rs === "accepted" || caseClosed;
  return (
    <div className="space-y-6" data-testid="advocate-case">
      <Link to="/advocate" data-testid="advocate-back" className="inline-flex items-center gap-1 text-sm text-[#A0AABF] hover:text-white transition-colors"><ChevronLeft className="w-4 h-4" strokeWidth={1.5}/> Back</Link>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] tracking-[0.32em] uppercase text-[#CBA96C]">Case {t.id.slice(0,8)}</div>
          <h1 className="font-serif-display text-3xl md:text-4xl text-white mt-1">{t.template_title || t.service_type.replace(/_/g,' ')}</h1>
          <div className="text-sm text-[#A0AABF] mt-1">Customer: {t.customer_name} ({t.customer_email})</div>
        </div>
        <div className="flex items-center gap-2">
          <StatusPill status={t.status} dim="status"/>
          <StatusPill status={rs} dim="review_status"/>
        </div>
      </div>

      {/* Admin returned this for rework — surface their reason prominently, it's the whole
          point of the rework loop (LIFECYCLE.md "review_status") */}
      {rs === "in_progress" && (t.review_rework_count || 0) > 0 && (
        <div className="rounded-xl border border-[#F5A524]/40 bg-[#F5A524]/10 px-4 py-3" data-testid="adv-rework-banner">
          <div className="text-sm text-[#F5A524] font-medium">Admin returned this review for rework</div>
          {/* Read from the transaction, not by searching its history: the case history lives in
              its own table now (backend/core/db/tables.py::AuditEvents) and lists no longer carry
              it, so "why was this returned" is a field on the case. */}
          {t.rework_note ? <div className="text-sm text-[#A0AABF] mt-1">“{t.rework_note}”</div> : null}
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle className="font-serif-display text-lg">Customer Submission</CardTitle></CardHeader>
          <CardContent className="text-sm space-y-1.5">
            {Object.entries(t.form_data||{}).map(([k,v]) => <div key={k} className="flex justify-between border-b border-dashed py-1"><span className="text-[#6B7280] capitalize">{k.replace(/_/g,' ')}</span><span className="text-right max-w-[60%]">{String(v)}</span></div>)}
            {/* Split by who attached it — "Customer uploads" stopped being an accurate heading
                for the whole list the moment admin gained a way to attach something here too
                (see AdminTransactionDetail.jsx's "Upload for customer"), same reasoning as the
                same split on admin's and the customer's own case pages. */}
            {(() => {
              const customerFiles = (t.documents || []).filter(f => f.uploader_role === "customer");
              const adminFiles = (t.documents || []).filter(f => f.uploader_role !== "customer");
              return (
                <div className="pt-3 mt-3 border-t">
                  <div className="text-xs uppercase tracking-widest text-[#6B7280] mb-2">Customer uploads</div>
                  {customerFiles.map(f => (
                    <a key={f.id} href={`${API}/files/${f.id}`} target="_blank" rel="noopener noreferrer" className="flex justify-between py-1 hover:text-white"><span className="text-sm">{f.name}</span><Download className="w-4 h-4"/></a>
                  ))}
                  {customerFiles.length === 0 && <div className="text-[#6B7280] text-sm">No customer uploads.</div>}
                  {adminFiles.length > 0 && (
                    <>
                      <div className="text-xs uppercase tracking-widest text-[#6B7280] mb-2 mt-3 pt-3 border-t">Admin uploads</div>
                      {adminFiles.map(f => (
                        <a key={f.id} href={`${API}/files/${f.id}`} target="_blank" rel="noopener noreferrer" className="flex justify-between py-1 hover:text-white"><span className="text-sm">{f.name}</span><Download className="w-4 h-4"/></a>
                      ))}
                    </>
                  )}
                </div>
              );
            })()}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="font-serif-display text-lg">Submit Report</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <Label>Upload PDF report</Label>
            <Input data-testid="adv-report-file" type="file" accept=".pdf" onChange={e=>setFile(e.target.files[0])}/>
            <Button data-testid="adv-upload-report" onClick={upload} className="w-full bg-[#CBA96C] text-[#080C16] hover:bg-[#DBC190]"><Upload className="w-4 h-4 mr-2"/>Upload Report</Button>
            <div className="pt-3 border-t">
              <div className="text-xs uppercase tracking-widest text-[#6B7280] mb-2">Existing reports</div>
              {(t.reports||[]).map(f => (
                <FileRow key={f.id} file={f} locked={t.status === "closed"} onDeleted={load} className="text-sm hover:text-white">
                  {f.visibility === "customer" && <span className="pill pill-xs pill-approved shrink-0">Shared</span>}
                </FileRow>
              ))}
              {(t.reports||[]).length===0 && <div className="text-[#6B7280] text-sm">No reports yet.</div>}
              <div className="text-xs text-[#6B7280] mt-2">Your reports are internal by default — admin decides whether to share one with the customer.</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* The explicit "my review is complete" signal. Before this existed, completion was
          inferred from any uploaded file or comment — a rough draft looked identical to
          finished work. See LIFECYCLE.md "review_status". */}
      <Card data-testid="adv-submit-review">
        <CardHeader>
          <CardTitle className="font-serif-display text-lg">
            {reviewDone ? "Review accepted" : awaitingAdmin ? "Review submitted — awaiting admin" : "Complete your review"}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {reviewDone && <div className="text-sm text-[#6B7280]">Admin has accepted this review. Nothing further is needed from you.</div>}
          {awaitingAdmin && <div className="text-sm text-[#6B7280]">Admin has your findings and will accept them or return the case with a reason.</div>}
          {!reviewDone && !awaitingAdmin && (
            <>
              <div>
                <Label className="text-xs text-[#6B7280]">Summary of your conclusion (required)</Label>
                <Textarea data-testid="adv-review-summary" rows={2} value={summary} onChange={e=>setSummary(e.target.value)}
                          placeholder="e.g. Document is in order; name must match the ID proof exactly." className="mt-1"/>
              </div>
              <div>
                <Label className="text-xs text-[#6B7280]">Corrections to the customer's details</Label>
                <div className="text-xs text-[#6B7280] mb-2">Leave blank to keep the customer's value. Admin can apply these to the document in one click.</div>
                <div className="grid sm:grid-cols-2 gap-2 max-h-52 overflow-y-auto pr-1">
                  {Object.entries(t.form_data || {}).map(([k, v]) => (
                    <div key={k}>
                      <Label className="text-[11px] text-[#6B7280] capitalize">{k.replace(/_/g,' ')}</Label>
                      <Input data-testid={`adv-correct-${k}`} value={corrections[k] ?? ""} placeholder={String(v)}
                             onChange={e=>setCorrections(c=>({...c, [k]: e.target.value}))} className="mt-0.5 h-8 text-sm"/>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-xs text-[#6B7280]">Recommended additional clauses</Label>
                <Textarea data-testid="adv-review-clauses" rows={3} value={clauses} onChange={e=>setClauses(e.target.value)}
                          placeholder="Any clause you recommend adding to the final document…" className="mt-1"/>
              </div>
              <div className="flex justify-end pt-2 border-t">
                <Button data-testid="adv-submit-review-btn" onClick={submitReview} disabled={!summary.trim()}
                        className="bg-[#CBA96C] text-[#080C16] hover:bg-[#DBC190] font-medium">Submit review to admin</Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Card id="case-messages">
        {/* Admin is the sole relay between reviewer and customer (LIFECYCLE.md "Message and
            audit-log visibility") — this thread is reviewer ↔ admin only, never the customer.
            Admin's quick-messages (one-way broadcasts from the queue) and the two-way comment
            relay used to be two separate panels here; merged into one chronological thread so it
            reads as a single conversation with admin, the same way the customer's and admin's own
            case pages already show it. */}
        <CardHeader><CardTitle className="font-serif-display text-lg flex items-center gap-2"><MessageSquare className="w-4 h-4"/> Communication with Admin</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="max-h-96 overflow-y-auto">
            {/* A customer message is here only because admin released it — labelFor says so,
                rather than letting it look like a direct customer channel that isn't. A
                quick-message's author_name is just the literal string "Admin" (no per-user
                identity is recorded for these), so the default "name · role" label would read
                as a redundant "Admin · Admin" — shown as a bare role instead. */}
            <ChatThread comments={thread} viewerRole="reviewer"
                        labelFor={(c) => c.author_role === "customer" ? "Customer (shared by admin)"
                                        : c.id?.startsWith("qm-") ? "Admin" : undefined}
                        emptyText="No messages yet. Anything you post here goes to the admin, who relays it to the customer if needed."/>
          </div>
          {/* A closed case is sealed to everyone — see LIFECYCLE.md "Terminal cases are sealed" */}
          {caseClosed ? (
            <div className="text-sm text-[#6B7280]">This case is closed — no further messages can be added.</div>
          ) : t.reviewer_channel_open ? (
            <div className="flex gap-2">
              <Textarea data-testid="adv-comment-input" value={text} onChange={e=>setText(e.target.value)} rows={2} placeholder="Message the admin about this case..."/>
              <Button data-testid="adv-comment-send" onClick={post} className="bg-[#CBA96C] text-[#080C16] hover:bg-[#DBC190]">Send</Button>
            </div>
          ) : (
            <div className="text-xs text-[#6B7280]">Admin hasn't opened the reply channel on this case yet — you can't reply until they do.</div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
